import { isEqual } from "lodash";
import { LatLng } from "geolocation-utils";

import { Utils } from "utils";
import { Places } from "services/places";
import { Prettify, Iterable, IPlace } from "types";
import { DEFAULT_ROUTE_DAY_HOURS } from "constants/constants";

export class Waypoint {
  //   private _order: number = 0;
  private _coordinate: LatLng;
  private _place: Iterable<IPlace> | undefined = undefined;

  constructor(coordinate: LatLng, place: Iterable<IPlace> | undefined) {
    this._place = place;
    this._coordinate = coordinate;
  }

  get coordinate() {
    return this._coordinate;
  }

  get place() {
    return this._place;
  }
}

interface WaypointsConstructor {
  order: number[];
  startPoint: LatLng;
  finishPoint?: LatLng;
  snappedWaypoints: LatLng[];
  places: Iterable<IPlace>[];
}

export class Waypoints {
  private start: LatLng;
  private order: number[];
  private finish?: LatLng;
  private snappedWaypoints: LatLng[];
  private places: Iterable<IPlace>[];

  constructor(props: Prettify<WaypointsConstructor>) {
    this.order = props.order;
    this.places = props.places;
    this.start = props.startPoint;
    this.finish = props.finishPoint;
    this.snappedWaypoints = props.snappedWaypoints;
  }

  // Public Getters

  public get waypoints() {
    return this.snappedWaypoints;
  }

  // Private Getters

  private get placesCoordinates() {
    const isLinearRoute = this.finish !== undefined;
    return isLinearRoute
      ? [undefined, ...this.places, undefined]
      : [undefined, ...this.places];
  }

  // Public Methods

  public getPlaceAtIndex(index: number) {
    const selectedOrder = this.order[index];
    return this.placesCoordinates.at(selectedOrder);
  }

  public at(index: number) {
    return new Waypoint(
      this.snappedWaypoints[index],
      this.getPlaceAtIndex(index)
    );
  }

  public atOrder(value: number) {}

  public findPlaceFor(item: LatLng) {
    const index = this.waypoints.findIndex(
      (i) => i.lat === item.lat && i.lng === item.lng
    );
    return this.getPlaceAtIndex(index);
  }
}

interface RouteWaypointsProps {
  coordinates: LatLng[];
  places: (Iterable<IPlace> | undefined)[];
  availablePlaces?: Iterable<IPlace>[];
}

export class RouteWayponts {
  private _coordinates: RouteWaypointsProps["coordinates"] = [];

  private _places: RouteWaypointsProps["places"] = [];
  private _availablePlaces: Iterable<IPlace>[] = [];
  private _suggestedPlaces: Iterable<IPlace>[] = [];

  constructor(props: RouteWaypointsProps) {
    this._places = props.places;
    this._coordinates = props.coordinates;
    this._availablePlaces = props.availablePlaces ?? [];
  }

  // Getters

  get places() {
    return this._places;
  }

  get waypoints() {
    return this._coordinates;
  }

  get availablePlaces() {
    return this._availablePlaces;
  }

  get existingPlaces() {
    return this.places.filter(Boolean) as Iterable<IPlace>[];
  }

  get suggestedPlaces() {
    return this._suggestedPlaces;
  }

  // Setters

  public set places(array: typeof this._places) {
    this._places = array;
  }

  public set waypoints(array: typeof this._coordinates) {
    this._coordinates = array;
  }

  public set suggestedPlaces(array: Iterable<IPlace>[]) {
    this._suggestedPlaces = array;
  }

  public set availablePlaces(array: Iterable<IPlace>[]) {
    this._availablePlaces = array;
  }

  // Public Methods

  removeWaypoints(waypoints: LatLng[]) {
    const indexes = waypoints.map((i) => this.findWaypointIndex(i));

    const updatedPlaces = this._places.filter((_, i) => !indexes.includes(i));
    const updatedWaypoints = this._coordinates.filter(
      (_, i) => !indexes.includes(i)
    );

    this._places = updatedPlaces;
    this._coordinates = updatedWaypoints;
  }

  public getPlaceForWaypoint(waypoint?: LatLng) {
    const waypointIndex = this.findWaypointIndex(waypoint);
    return this._places.at(waypointIndex);
  }

  public findWaypointIndex(waypoint: LatLng | undefined) {
    if (!waypoint) return -1;
    return this.waypoints.findIndex(
      (item) => item.lat === waypoint.lat && item.lng === waypoint.lng
    );
  }

  public findPlaceIndex(place: Iterable<IPlace> | undefined) {
    if (!place) return -1;
    return this.places.findIndex((item) => item?.id === place.id);
  }

  public findWaypointIndexIn(
    waypoint: LatLng | undefined,
    inCoordinates: LatLng[],
    order: "first" | "last" = "first"
  ) {
    if (!waypoint) return -1;
    return order === "first"
      ? inCoordinates.findIndex((item) => isEqual(item, waypoint))
      : inCoordinates.findLastIndex((item) => isEqual(item, waypoint));
  }

  public findPlaceIndexIn(
    place: Iterable<IPlace> | undefined,
    inCoordinates: LatLng[],
    order: "first" | "last" = "first"
  ) {
    if (!place) return -1;
    const index = this.findPlaceIndex(place);
    const waypoint = this.waypoints.at(index);

    if (!waypoint) return -1;
    return this.findWaypointIndexIn(waypoint, inCoordinates, order);
  }

  public removeByIndexes(indexes: number[]) {
    const waypoints = indexes.map((index) => this.waypoints[index]);
    this.removeWaypoints(waypoints);
  }

  reduceOneRouteDay() {
    const filtered = this.places.filter(Boolean) as Iterable<IPlace>[];
    const places = new Places(filtered);
    places.sortByRating();

    const oneDayInMinutes = DEFAULT_ROUTE_DAY_HOURS * 60;
    const reducePlacesIds: string[] = [];
    const list = [...places.getList()];
    list.reverse();

    for (const [index, place] of list.entries()) {
      const breaktimes = list
        .slice(0, index + 1)
        .reduce((acc, item) => acc + (item.breaktime ?? 0), 0);

      if (breaktimes > oneDayInMinutes) break;
      reducePlacesIds.push(place.id);
    }

    const updatedPlaces: typeof this._places = [];
    const updatedWaypoints: typeof this._coordinates = [];

    console.log({ places: this.places, waypoints: this.waypoints });

    for (const index in this.places) {
      const i = Number(index);
      const currentPlace = this.places.at(i);
      const currentWaypoint = this.waypoints[i];

      if (currentPlace && reducePlacesIds.includes(currentPlace.id)) {
        continue;
      }

      updatedPlaces.push(currentPlace);
      updatedWaypoints.push(currentWaypoint);
    }

    // console.log({
    //   reducedPlaces: reducePlacesIds.map((id) => {
    //     const place = this.places.find((item) => item?.id === id);
    //     return {
    //       name: place?.title ?? "-/-",
    //       breaktime: place?.breaktime ?? 0,
    //     };
    //   }),
    // });

    return { updatedPlaces, updatedWaypoints };
  }

  public reducePlacesCount(reduceCount: number) {
    const filteredPlaces = this.places.filter(Boolean) as Iterable<IPlace>[];

    const maxRating = Utils.Places.calculateMaxRatingFor(filteredPlaces);
    const maxUserRating =
      Utils.Places.calculateMaxUserRatingFor(filteredPlaces);

    const sortedPlaces = filteredPlaces.sort((a, b) => {
      const aRating = Utils.Places.calculateCustomRating(
        a,
        maxRating,
        maxUserRating
      );
      const bRating = Utils.Places.calculateCustomRating(
        b,
        maxRating,
        maxUserRating
      );
      return bRating - aRating;
    });

    const availableCount = sortedPlaces.length;
    const maxCount =
      reduceCount > availableCount ? availableCount : reduceCount;

    const slice = sortedPlaces.slice(0, maxCount);

    let updatedWaypoints: LatLng[] = [];
    let updatedPlaces: (Iterable<IPlace> | undefined)[] = [];

    const availableIDs = slice.map((item) => item.id);

    for (const placeIndex in this.places) {
      const index = Number(placeIndex);
      const currentPlace = this.places.at(index);
      const currentWaypoint = this.waypoints[index];

      if (currentPlace && !availableIDs.includes(currentPlace.id)) {
        continue;
      }

      updatedPlaces.push(currentPlace);
      updatedWaypoints.push(currentWaypoint);
    }

    return { updatedPlaces, updatedWaypoints };
  }
}
